import Add20 from '@carbon/icons-react/es/add/20';
import ChevronSort16 from '@carbon/icons-react/es/chevron--sort/16';
import Group16 from '@carbon/icons-react/es/group/16';
import Home20 from '@carbon/icons-react/es/home/20';
import Notification20 from '@carbon/icons-react/es/notification/20';
import { useBoolean } from 'ahooks';
import { Dropdown } from 'antd';
import React, { FC } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { Company, ProjectConversationId, ProjectId, SpaceId } from 'api/models';
import { ProjectDetailTabKeys, tabApiEnumMap } from 'api/project';
import { ProjectInUserProfile } from 'api/user';
import { ReactComponent as SlackLogo } from 'assets/svgs/slack-logo.svg';
import { Avatar } from 'components/atoms/avatar';
import { FCWidget } from 'components/atoms/freshchat-widget';
import { IconShell } from 'components/atoms/icon-shell';
import { SpaceIcon } from 'components/atoms/space-icon';
import {
  useFeatureEnabledProjectTabs,
  useProjectVisibilityConfig,
} from 'components/molecules/project-details/hooks';
import { useProjectConversationTotalUnreadCount } from 'components/organisms/projects/project-conversation';
import { RoutePaths } from 'constant';
import { Space as SpaceT } from 'data/spaces';
import { useProjectSpaces } from 'data/spaces/hooks';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { useAccountDataQuery } from 'queries/account';
import { usePending } from 'stores/notifications';
import { getDevEnv } from 'utils/is-localhost';
import { getSpaceDetailUrl } from 'utils/urls';
import { getIcon, tabRoutePathMap, useConversationIdFromUrl } from './helpers';
import { PoweredBy, Rocketlane } from './powered-by-rocketlane';
import {
  ChatWithUsButton,
  Divider,
  DropdownMenu,
  DropdownMenuItem,
  Empty,
  Footer,
  Header,
  InfoRight,
  IntendedMenuItem,
  Logo,
  LogoContainer,
  Menu,
  MenuItemLg,
  Navigation,
  NotifBadge,
  ProjectMenuItem,
  ProjectName,
  ProjectNameWrapper,
  Sider,
  SpaceWrapper,
  StyledBadge,
  StyledSidebar,
  SubMenu,
  Trigger,
} from './styled';

interface SidebarProps {
  vendor: Company;
  customer: Company;
  projects: ProjectInUserProfile[];
  selectedProject: ProjectInUserProfile;
  selectedItem: string;
  onProjectChange: (projectId: ProjectId) => void;
}

export const Sidebar: FC<SidebarProps> = ({
  vendor,
  customer,
  selectedItem,
  selectedProject,
  projects,
  onProjectChange,
}) => {
  const { projectId } = selectedProject;
  const [visible, { toggle }] = useBoolean(false);
  const hasMoreProjects = projects.length > 1;
  const { data: accountData } = useAccountDataQuery();
  const { isLocal } = getDevEnv();

  const accountMeta = accountData?.accountMeta;

  const { data: spaces, isLoading } = useProjectSpaces(
    ProjectId(selectedProject.projectId),
  );
  const [pending] = usePending();

  const { isPending, isPreview } = useCustomerPreview();
  const notPreview = !isPending && !isPreview;

  const renderThumb = ({ style, ...props }) => {
    const thumbStyle = {
      backgroundColor: 'rgba(255,255,255, 0.4)', // this value is generated based on dark background of the sidebar
    };

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <div style={{ ...style, ...thumbStyle }} {...props} />;
  };
  const contentHeight =
    window.innerHeight -
    276; /* 276 is the total height of header (132) and footer (144) */

  // useParams won't work here as Sidebar don't have the Router HOC.
  const chatId = useConversationIdFromUrl();

  // Only sent convId if user is on conversations page
  const activeConversationId = chatId
    ? ProjectConversationId(Number(chatId))
    : undefined;

  const unreadConversationsCount = useProjectConversationTotalUnreadCount(
    ProjectId(projectId),
    activeConversationId,
  );
  const featureEnabledprojectTabs = useFeatureEnabledProjectTabs();

  const config = useProjectVisibilityConfig(ProjectId(projectId));
  const projectVisibleTabs = featureEnabledprojectTabs.filter((tab) => {
    if (config) {
      return config.visibilityMeta?.[tabApiEnumMap[tab.id]];
    }

    return true;
  });

  const tabsWithRoutes = projectVisibleTabs
    .filter((tab) => {
      // Filter out some tabs. (Like form responses in customer portal)
      return Boolean(tabRoutePathMap[tab.id]);
    })
    .map((tab) => {
      return { ...tab, getRoute: tabRoutePathMap[tab.id] };
    });

  const projectTabs = tabsWithRoutes.map((tab) => {
    // Show different component for chat icon
    if (tab.id === ProjectDetailTabKeys.Chat) {
      return {
        ...tab,
        titleContent: (
          <InfoRight>
            <FormattedMessage id="projects.conversations.title" />
            <StyledBadge count={unreadConversationsCount} />
          </InfoRight>
        ),
      };
    }

    return {
      ...tab,
      titleContent: <FormattedMessage id={tab.translationKey} />,
    };
  });

  return (
    <Sider>
      <StyledSidebar>
        <Header>
          <LogoContainer direction="horizontal">
            <Logo name={vendor.companyName} src={vendor.companyLogoUrl} />
            <Add20 color="var(--white-100)" />
            <Logo name={customer.companyName} src={customer.companyLogoUrl} />
          </LogoContainer>
        </Header>
        <Scrollbars
          renderThumbVertical={renderThumb}
          autoHeight
          autoHeightMin={contentHeight}
          autoHeightMax={contentHeight}
        >
          <Navigation>
            <Menu
              mode="inline"
              selectedKeys={[selectedItem]}
              defaultOpenKeys={['shared-spaces']}
            >
              <MenuItemLg icon={<Home20 />} key={RoutePaths.home}>
                <Link to="/">
                  <FormattedMessage id="home.title" />
                </Link>
              </MenuItemLg>
              <MenuItemLg
                icon={
                  <NotifBadge text={<Notification20 />} pending={pending} />
                }
                key={RoutePaths.notifications}
              >
                <Link to={RoutePaths.notifications}>
                  <FormattedMessage id="notifications" />
                </Link>
              </MenuItemLg>
              <MenuItemLg
                icon={<SlackLogo />}
                key={RoutePaths.slackIntegration}
              >
                <Link to={RoutePaths.slackIntegration}>
                  <FormattedMessage id="settings.integrations.slack.title" />
                </Link>
              </MenuItemLg>
              <Divider />
              {hasMoreProjects ? (
                <ProjectMenuItem
                  onClick={() => hasMoreProjects && toggle()}
                  icon={
                    <Avatar
                      size="small"
                      shape="square"
                      name={selectedProject.projectName.split(' ')[0]}
                    />
                  }
                >
                  <Dropdown
                    visible={visible}
                    disabled={!hasMoreProjects}
                    overlay={
                      <DropdownMenu
                        mode="inline"
                        onClick={({ key }) =>
                          onProjectChange(ProjectId(Number(key)))
                        }
                        selectedKeys={[String(selectedProject.projectId)]}
                      >
                        {projects.map((p) => (
                          <DropdownMenuItem
                            key={p.projectId}
                            icon={
                              <Avatar
                                shape="square"
                                size="small"
                                name={p.projectName.split(' ')[0]}
                              />
                            }
                          >
                            <ProjectName>{p.projectName}</ProjectName>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenu>
                    }
                  >
                    <Trigger hasMore={hasMoreProjects}>
                      <ProjectName>{selectedProject.projectName}</ProjectName>
                      {hasMoreProjects && <ChevronSort16 />}
                    </Trigger>
                  </Dropdown>
                </ProjectMenuItem>
              ) : (
                <ProjectNameWrapper>
                  <Avatar
                    size="small"
                    shape="square"
                    name={selectedProject.projectName.split(' ')[0]}
                  />
                  <ProjectName title={selectedProject.projectName}>
                    {selectedProject.projectName}
                  </ProjectName>
                </ProjectNameWrapper>
              )}

              {projectTabs.map((tab) => {
                return (
                  <IntendedMenuItem
                    icon={getIcon(tab.icon)}
                    key={tab.getRoute && tab.getRoute(ProjectId(projectId))}
                  >
                    <Link
                      to={tab.getRoute && tab.getRoute(ProjectId(projectId))}
                    >
                      {tab.titleContent}
                    </Link>
                  </IntendedMenuItem>
                );
              })}
              <SubMenu
                key="shared-spaces"
                icon={<Group16 />}
                title={<FormattedMessage id="projects.spaces.shared" />}
              >
                {isLoading && (
                  <SpaceWrapper>
                    <FormattedMessage id="loading" />
                  </SpaceWrapper>
                )}
                {spaces.length === 0 && !isLoading && (
                  <Empty>
                    <FormattedMessage id="space.shared.empty" />
                  </Empty>
                )}
                {!isLoading &&
                  spaces
                    .filter((s) => !s.private)
                    .map((space: SpaceT) => (
                      <Menu.Item
                        icon={
                          <IconShell
                            icon={<SpaceIcon name={space.spaceName} />}
                          />
                        }
                        key={getSpaceDetailUrl(SpaceId(1, space.spaceId))}
                      >
                        <Link
                          to={getSpaceDetailUrl(
                            SpaceId(projectId, space.spaceId),
                          )}
                        >
                          {space.spaceName}
                        </Link>
                      </Menu.Item>
                    ))}
              </SubMenu>
            </Menu>
          </Navigation>
        </Scrollbars>
        <Footer>
          {!isLocal && notPreview && (
            <>
              <FCWidget
                left={250}
                render={(openFCWidget) => {
                  return (
                    <ChatWithUsButton
                      size="large"
                      type="link"
                      shape="circle"
                      onClick={openFCWidget}
                    >
                      <FormattedMessage id="menu.label.chat" />
                    </ChatWithUsButton>
                  );
                }}
              />
              {accountMeta != null ? (
                accountMeta?.features?.disablePoweredBy ? null : (
                  <PoweredBy>
                    <Rocketlane />
                  </PoweredBy>
                )
              ) : null}
            </>
          )}
        </Footer>
      </StyledSidebar>
    </Sider>
  );
};
